import CognitoIdentityServiceProvider from 'aws-sdk/clients/cognitoidentityserviceprovider'
import { GET_USER_SUBSCRIPTION } from '@queries/userSubscription'
import { getClient } from '@utils/aws-graphql'
import {
  ICognitoSearch,
  ICognitoState,
  UserCognito,
  CreateUserCognito,
  CreateUserCognitoInput,
} from 'interfaces/UserCognito'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import { getAuthAdapter } from '@shell/adapters/auth'

let cispInstance: CognitoIdentityServiceProvider | undefined

interface IUserSubscriptionResponse {
  customerId2A?: string
  customerId2B?: string
  email?: string
  userName2a?: string
  userName2b?: string
  userType?: number
}

/**
 * Creates (or refreshes) a CognitoIdentityServiceProvider client with current credentials.
 * Throws if the user is not authenticated — callers already wrap this in try/catch.
 */
export const configureAWSCognito = async (): Promise<CognitoIdentityServiceProvider> => {
  const credentials = await getAuthAdapter().getCredentials()
  cispInstance = new CognitoIdentityServiceProvider({
    apiVersion: '2016-04-18',
    region: process.env.AWS_PROJECT_REGION,
    credentials,
  })
  return cispInstance
}

/** Clear cached client on sign-out so stale credentials are never reused */
export const resetCognitoClient = (): void => {
  cispInstance = undefined
}

const mapToCognitoState = (response: any) =>
  map((response || {}).Users, (user) => {
    const attrs = get(user, 'Attributes', [])
    const nu: any = reduce(
      attrs,
      (accumulator, attr) => {
        accumulator = {
          ...accumulator,
          [attr.Name]: attr.Value,
        }

        return accumulator
      },
      {}
    )

    return {
      username: user.Username,
      name: nu.name || nu.given_name || user.Username,
      email: nu.email,
      customerId: nu['custom:customerId'],
      customerId2b: nu['custom:customerId2b'],
      myBerlitzEnv: nu['custom:MyBerlitzEnvironment'],
      sfEnv: nu['custom:sfEnv'],
      status: user.UserStatus,
      enabled: user.Enabled,
      timezone: nu.zoneinfo,
    } as UserCognito
  })

// This function calls the subscription GraphQL to get the subscription info by emails
const getSubscriptionsByEmails = async (emails: string[]): Promise<Record<string, IUserSubscriptionResponse>> => {
  if (!emails.length) {
    console.log(`Empty email payload for subscription query`)
    return {}
  }

  let subscriptions: unknown = []

  try {
    const client = await getClient()
    const { data } = await client.query({
      query: GET_USER_SUBSCRIPTION,
      variables: {
        input: { emails },
      },
      fetchPolicy: 'network-only',
    })

    subscriptions = get(data, 'getUserSubscriptions', [])
  } catch (err) {
    __DEV__ &&
      console.log('subscription api err: %o', {
        emails,
        message: err?.message,
        code: err?.code,
        status: err?.response?.status,
        data: err?.response?.data,
      })
    return {}
  }

  const parsed = typeof subscriptions === 'string' ? JSON.parse(subscriptions) : subscriptions

  // Ensure it's an array with at least one object
  if (!Array.isArray(parsed) || !parsed.length || typeof parsed[0] !== 'object') {
    console.error('Invalid subscription response format', {
      parsed,
    })

    throw new Error('Invalid subscription response format')
  }

  return parsed[0] as Record<string, IUserSubscriptionResponse>
}

// This function takes the list of users from cognito and override the customerIds with the ones from subscription if exists
const overrideCustomerIds = async (users: UserCognito[]): Promise<UserCognito[]> => {
  // get unique emails from the users list
  const emails = Array.from(new Set(users.map((user) => user.email).filter(Boolean)))

  if (!emails.length) {
    return users
  }

  const subscriptionByEmail = await getSubscriptionsByEmails(emails)

  return users.map((user) => {
    const subscription = subscriptionByEmail[user.email]

    if (!subscription) {
      return user
    }

    let customerId2A = subscription.customerId2A || user.customerId
    let customerId2B = subscription.customerId2B || user.customerId2b

    // userType = 1 -> Student, userType = 2 -> Instructor
    if (subscription.userType === 2) {
      customerId2A = subscription.email || user.customerId
      customerId2B = subscription.email || user.customerId2b
    }

    return {
      ...user,
      customerId: customerId2A,
      customerId2b: customerId2B,
    }
  })
}

export const listUsersInGroup = async (limit: number, group: string, pagination?: string) => {
  let cognito = {
    users: [] as UserCognito[],
    next: '',
    prev: '',
  } as ICognitoState
  const cisp = await configureAWSCognito()
  try {
    const params: any = {
      UserPoolId: `${process.env.AWS_USER_POOLS_ID}`,
      Limit: limit,
      GroupName: group,
    }

    if (pagination) {
      params.NextToken = pagination
    }

    const response = await cisp.listUsersInGroup(params).promise()
    cognito.users = await overrideCustomerIds(mapToCognitoState(response))
    cognito.next = response.NextToken || ''
  } catch (err) {
    __DEV__ && console.log('err: %o', err)
  }

  return cognito
}

export const adminDisableUser = async (user: UserCognito) => {
  let ret = false
  const cisp = await configureAWSCognito()
  try {
    const params: any = {
      UserPoolId: `${process.env.AWS_USER_POOLS_ID}`,
      Username: user.username,
    }

    await cisp.adminDisableUser(params).promise()
    ret = true
  } catch (err) {
    __DEV__ && console.log('err: %o', err)
  }

  return ret
}

export const adminEnableUser = async (user: UserCognito) => {
  let ret = false
  const cisp = await configureAWSCognito()
  try {
    const params: any = {
      UserPoolId: `${process.env.AWS_USER_POOLS_ID}`,
      Username: user.username,
    }

    await cisp.adminEnableUser(params).promise()
    ret = true
  } catch (err) {
    __DEV__ && console.log('err: %o', err)
  }

  return ret
}

export const listUsers = async (search: ICognitoSearch, limit: number, pagination?: string) => {
  let cognito = {
    users: [] as UserCognito[],
    next: '',
    prev: '',
  } as ICognitoState
  const cisp = await configureAWSCognito()
  try {
    const params: any = {
      UserPoolId: `${process.env.AWS_USER_POOLS_ID}`,
      Filter: `${search.attribute} ^= \'${search.searchKey}\'`,
      Limit: limit,
    }

    if (pagination) {
      params.PaginationToken = pagination
    }

    const response = await cisp.listUsers(params).promise()
    cognito.users = await overrideCustomerIds(mapToCognitoState(response))
    cognito.next = response.PaginationToken || ''
  } catch (err) {
    __DEV__ && console.log('err: %o', err)
  }

  return cognito
}

export const adminListGroupForUser = async (user: UserCognito) => {
  const cisp = await configureAWSCognito()
  try {
    const params: any = {
      UserPoolId: `${process.env.AWS_USER_POOLS_ID}`,
      Username: user.username,
    }

    const response = await cisp.adminListGroupsForUser(params).promise()
    const attrs = get(response, 'Groups', [])
    const group: CognitoIdentityServiceProvider.GroupType = reduce(
      attrs,
      (accumulator, group) => {
        if (isEmpty(accumulator)) {
          accumulator = group
        }

        if (get(group, 'Precedence', 0) > get(accumulator, 'Precedence', 0)) {
          accumulator = group
        }

        return accumulator
      },
      {} as CognitoIdentityServiceProvider.GroupType
    )
    return group
  } catch (err) {
    __DEV__ && console.log('err: %o', err)
  }
}

export const adminCreateUser = async (user: UserCognito) => {
  let ret = false
  const cisp = await configureAWSCognito()
  try {
    const params: any = {
      UserPoolId: `${process.env.AWS_USER_POOLS_ID}`,
      Username: user.username,
      MessageAction: 'RESEND',
    }

    await cisp.adminCreateUser(params).promise()
    ret = true
  } catch (err) {
    __DEV__ && console.log('err: %o', err)
  }
  return ret
}

export const adminSetUserPassword = async (user: UserCognito, password: string) => {
  const cisp = await configureAWSCognito()
  try {
    const params: any = {
      UserPoolId: `${process.env.AWS_USER_POOLS_ID}`,
      Username: user.username,
      Password: password,
      Permanent: false,
    }

    return cisp.adminSetUserPassword(params).promise()
  } catch (err) {
    __DEV__ && console.log('err: %o', err)
  }
}

export const adminCreateUserV2 = async (user: CreateUserCognito) => {
  let ret = false
  const cisp = await configureAWSCognito()
  try {
    const params: any = {
      TemporaryPassword: `${process.env.TEMP_ADMIN_PASSWORD}`,
      UserAttributes: [
        {
          Name: 'custom:customerId',
          Value: 'integrationTeacherTest',
        },
        {
          Name: 'email',
          Value: user.email,
        },
        {
          Name: 'email_verified',
          Value: 'true',
        },
      ],
      Username: user.username,
      UserPoolId: `${process.env.AWS_USER_POOLS_ID}`,
    }

    await cisp.adminCreateUser(params).promise()
    ret = true
  } catch (err) {
    __DEV__ && console.log('err: %o', err)
  }
  return ret
}

export const adminAddUserToGroup = async (user: CreateUserCognitoInput) => {
  let ret = false
  const cisp = await configureAWSCognito()
  try {
    const params: any = {
      GroupName: user.groupName,
      Username: user.username,
      UserPoolId: `${process.env.AWS_USER_POOLS_ID}`,
    }

    await cisp.adminAddUserToGroup(params).promise()
    ret = true
  } catch (err) {
    __DEV__ && console.log('err: %o', err)
  }
  return ret
}
